<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { SearchArticle } from '@/common/api/models.pleased';
import ArticleListItem from './ArticleListItem.vue';
import loading from '@/components/loading';
import { emitArticleAutocompleteFocused, emitOpenArticle } from '@/common/eventBus';
import SearchIcon from './icons/SearchIcon.vue';
import * as PleasedApi from '@/common/api/api.pleased';
import { reportingService } from '@/common/reporting';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics } from '@/common/logs/EventLogger';

@Component({
  directives: { loading, trackClick },
  components: {
    ArticleListItem,
    SearchIcon,
  },
})
export default class RelatedArticles extends Vue {
  @Prop({ type: Number, required: true }) articleId!: number;

  readonly INITIAL_ARTICLE_COUNT = 3;
  readonly BTN_EVTS = BTN_EVTS;

  isShowingAllArticles = false;
  articles: SearchArticle[] = [];

  get showArticleLimit() {
    return this.isShowingAllArticles ? this.articles.length : this.INITIAL_ARTICLE_COUNT;
  }

  get showArticles() {
    return this.articles.slice(0, this.showArticleLimit);
  }

  openArticle(item: SearchArticle) {
    reportingService.selectedArticleFromRelated(item.id);
    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_RELATED_ARTICLE,
      selection: this.articleId,
      metadata: { relatedArticle: item.id, title: item.title },
    });

    emitOpenArticle({
      isOpen: true,
      articleId: item.id,
      fetchArticle: true,
      isFromAutocomplete: true,
      categoryName: item.categoryName,
    });
  }

  @Watch('articleId', { immediate: true })
  async loadRelatedArticles() {
    const relatedArticles = await PleasedApi.getRelatedArticles.cached({ articleId: this.articleId });
    this.articles = relatedArticles.data;
    this.isShowingAllArticles = this.articles.length <= this.INITIAL_ARTICLE_COUNT;
  }

  handleShowMore() {
    this.isShowingAllArticles = true;
  }

  searchMore() {
    emitArticleAutocompleteFocused(true);
  }
}
</script>

<template>
  <div v-if="articles.length > 0" class="container">
    <span class="title">{{ $t('page.article.related.title') }}</span>
    <ul>
      <ArticleListItem v-for="item in showArticles" :key="item.id" :item="item" @mousedown="openArticle(item)" />
    </ul>
    <div>
      <div v-if="!isShowingAllArticles" class="button" @click="handleShowMore">
        {{ $t('page.article.related.view_more') }}
      </div>
      <div
        v-if="isShowingAllArticles"
        v-track-click="{
          name: BTN_EVTS.ARTICLE_SEARCH_BOTTOM,
          selection: articleId,
        }"
        class="button search-more-button"
        @click="searchMore"
      >
        <SearchIcon />
        {{ $t('page.article.related.search_more') }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

.container {
  display: flex;
  gap: 12px;
  flex-direction: column;
  border-top: 1px solid $article-feedback-border-color;

  padding: 30px 0;
  margin: 0 20px;
}

.title {
  @extend .text-bold;
  font-size: $size-m;
  color: $article-title-color;
  padding-bottom: 12px;
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    margin: 0 -16px;
  }
}

.button {
  font-size: 12px;
  padding: 4px;
  font-weight: 500;
  color: $article-link-color;
  cursor: pointer;
}

.search-more-button {
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
